import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ScrollText, Loader2, ShieldCheck, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);

const Stat = ({ label, value, color }) => (
  <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] px-5 py-3 min-w-[150px]">
    <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">{label}</div>
    <div className="text-lg font-serif mt-1" style={{ color }}>{value}</div>
  </div>
);

export default function PublicFundingRegistry() {
  const [programs, setPrograms] = useState(null);
  const [reserve, setReserve] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/funding/programs`);
        if (alive) { setPrograms(r.data.programs || []); setReserve(r.data.reserve || null); }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Unable to load the funding registry.");
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="funding-registry-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="fr-home-link">← Return to Home</Link>
        <div className="mt-5 flex items-center gap-3">
          <ScrollText size={24} className="text-[#B8860B]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">SEUTRADE FINANCIAL SERVICES</div>
            <h1 className="text-2xl font-serif">Master Private Negotiable Instrument Funding Registry</h1>
          </div>
        </div>
        <p className="text-[13px] text-[#9A9A90] mt-3 max-w-2xl leading-relaxed">
          A public registry of active revolving private funding programs. Each program is governed by one executed Master
          Private Negotiable Instrument Funding Agreement, under which individual private negotiable instruments are issued — each separately
          identifiable and independently traceable to its Master Agreement. Counterparties may verify any issued draft for discount.
        </p>

        {reserve && (
          <div className="mt-6 flex flex-wrap gap-4 items-center bg-[#0F0F0A] border border-[#2A2A22] rounded-xl p-5" data-testid="fr-reserve">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#B8860B] font-bold">Internal Credit Reserve</div>
            <Stat label="Reserve capacity" value={fmtUSD(reserve.capacity)} color="#C9A227" />
            <Stat label="Committed (outstanding)" value={fmtUSD(reserve.committed)} color="#9DB4E8" />
            <Stat label="Available" value={fmtUSD(reserve.available)} color="#3DBB8E" />
            <div className="text-[11px] text-[#8A8A8A] max-w-xs">Every issued draft is vaulted with a S.T.A.M.P. medallion and backed by this reserve at a {Math.round((reserve.ratio || 0) * 100)}% ratio.</div>
          </div>
        )}


        {error ? (
          <div className="mt-8 text-[#FF8585] text-sm" data-testid="fr-error">{error}</div>
        ) : !programs ? (
          <div className="mt-16 text-center" data-testid="fr-loading"><Loader2 className="animate-spin inline text-[#B8860B]" /></div>
        ) : programs.length === 0 ? (
          <div className="mt-10 text-[#5C5C5C] text-sm" data-testid="fr-empty">No active funding programs are currently published.</div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="fr-list">
            {programs.map((p) => (
              <div key={p.program_no} className="bg-[#13130E] border border-[#2A2A22] rounded-xl p-6" data-testid={`fr-program-${p.program_no}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[#E4C97A] text-[13px]">{p.program_no}</span>
                      {p.executed && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full px-2 py-0.5"><ShieldCheck size={11} /> Executed & notarized</span>}
                    </div>
                    <div className="text-lg font-serif mt-1">{p.program_name}</div>
                    <div className="text-[12px] text-[#8A8A8A] mt-1">Issuer: {p.issuer_name} · Acceptor: {p.acceptor_name || "—"} · {p.governing_law}</div>
                  </div>
                  <Link to={`/funding/register/${p.register_token}`} data-testid={`fr-open-${p.program_no}`} className="inline-flex items-center gap-1.5 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
                    View register <ArrowRight size={13} />
                  </Link>
                </div>
                <div className="flex flex-wrap gap-4 mt-5">
                  <Stat label="Revolving capacity" value={fmtUSD(p.program_limit)} color="#C9A227" />
                  <Stat label="Outstanding" value={fmtUSD(p.outstanding_total)} color="#9DB4E8" />
                  <Stat label="Available headroom" value={fmtUSD(p.available_headroom)} color="#3DBB8E" />
                  <Stat label="Drafts issued" value={p.drafts_count || 0} color="#E4C97A" />
                </div>
                {p.execution_hash && <div className="mt-4 text-[10px] font-mono text-[#5C5C5C] break-all">Agreement SHA-256: {p.execution_hash}</div>}
              </div>
            ))}
          </div>
        )}
        <p className="mt-8 text-[11px] text-[#5C5C5C] leading-relaxed max-w-2xl">
          This registry is informational and confirms the issuance status of instruments under the referenced Master Agreement.
          It does not constitute an offer of securities or a solicitation.
        </p>
      </div>
    </div>
  );
}
